/**
 * FormSection Component
 *
 * A reusable form section wrapper that provides consistent structure and styling
 * for grouping related form fields with proper accessibility and spacing.
 */

import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { ReactNode } from 'react';

export interface FormSectionProps {
  /**
   * Section title - displayed prominently at the top
   */
  title: string;

  /**
   * Optional subtitle or description for the section
   */
  subtitle?: string;

  /**
   * Form fields and content to render inside the section
   */
  children: ReactNode;

  /**
   * Visual style variant for the section
   * - 'card': Wrapped in a Material UI Card (default)
   * - 'inline': Simple container with divider
   * - 'collapsible': Accordion-style collapsible section
   */
  variant?: 'card' | 'inline' | 'collapsible';

  /**
   * Whether the section is required (affects title styling)
   */
  required?: boolean;

  /**
   * Whether to show an error state
   */
  error?: boolean;

  /**
   * Error message to display when error is true
   */
  errorMessage?: string;

  /**
   * Additional CSS class name for custom styling
   */
  className?: string;

  /**
   * For collapsible sections, whether initially expanded
   */
  defaultExpanded?: boolean;

  /**
   * For collapsible sections, controlled expanded state
   */
  expanded?: boolean;

  /**
   * For collapsible sections, expand/collapse handler
   */
  onToggle?: (expanded: boolean) => void;

  /**
   * ID for the section - used for accessibility
   */
  id?: string;

  /**
   * ARIA label for the section
   */
  'aria-label'?: string;
}

const FormSection = ({
  title,
  subtitle,
  children,
  variant = 'card',
  required = false,
  error = false,
  errorMessage,
  className,
  defaultExpanded = true,
  expanded,
  onToggle,
  id,
  'aria-label': ariaLabel,
}: FormSectionProps) => {
  const sectionId = id || `form-section-${title.toLowerCase().replace(/\s+/g, '-')}`;

  // Common header content
  const headerContent = (
    <Box>
      <Typography
        variant="h6"
        component="h2"
        id={`${sectionId}-title`}
        sx={{
          color: error ? 'error.main' : 'text.primary',
          fontWeight: 'medium',
        }}
      >
        {title}
        {required && (
          <Typography
            component="span"
            color="error.main"
            sx={{ ml: 0.5 }}
            aria-label="required"
          >
            *
          </Typography>
        )}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5 }}
          id={`${sectionId}-subtitle`}
        >
          {subtitle}
        </Typography>
      )}
      {error && errorMessage && (
        <Typography
          variant="body2"
          color="error.main"
          sx={{ mt: 0.5 }}
          role="alert"
          aria-live="polite"
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );

  // Common content wrapper
  const contentWrapper = (content: ReactNode) => (
    <Box
      role="group"
      aria-labelledby={`${sectionId}-title`}
      aria-describedby={subtitle ? `${sectionId}-subtitle` : undefined}
      aria-label={ariaLabel}
    >
      <Stack spacing={3}>{content}</Stack>
    </Box>
  );

  if (variant === 'collapsible') {
    return (
      <Accordion
        className={className}
        defaultExpanded={defaultExpanded}
        expanded={expanded}
        onChange={(_, isExpanded) => onToggle?.(isExpanded)}
        sx={{
          '&:before': {
            display: 'none',
          },
          boxShadow: theme => theme.shadows[1],
          borderRadius: theme => theme.customTokens.radii.lg,
          '&:not(:last-child)': {
            borderBottom: 0,
          },
          '&:first-of-type': {
            borderTopLeftRadius: theme => theme.customTokens.radii.lg,
            borderTopRightRadius: theme => theme.customTokens.radii.lg,
          },
          '&:last-of-type': {
            borderBottomLeftRadius: theme => theme.customTokens.radii.lg,
            borderBottomRightRadius: theme => theme.customTokens.radii.lg,
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${sectionId}-content`}
          id={`${sectionId}-header`}
        >
          {headerContent}
        </AccordionSummary>
        <AccordionDetails id={`${sectionId}-content`}>
          {contentWrapper(children)}
        </AccordionDetails>
      </Accordion>
    );
  }

  if (variant === 'inline') {
    return (
      <Box className={className}>
        {headerContent}
        <Divider sx={{ my: 2 }} />
        {contentWrapper(children)}
      </Box>
    );
  }

  // Default: card variant
  return (
    <Card
      className={className}
      sx={{
        borderRadius: theme => theme.customTokens.radii.lg,
        border: error ? '1px solid' : 'none',
        borderColor: error ? 'error.main' : 'transparent',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {headerContent}
        <Box sx={{ mt: 2 }}>{contentWrapper(children)}</Box>
      </CardContent>
    </Card>
  );
};

export default FormSection;
